import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { ReleaseOverview } from 'fym-shared/src/music24/releases';
import { TypedMemo } from '../../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../../components/primitives/Panel';
import { platformMeta } from '../../platformMeta';
import { SocialPlatform } from '../../types';
import { overviewMessages } from './overviewMessages';
import { OverviewPostRow } from './OverviewPostRow';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Overview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DonePanelProps {
  overview: ReleaseOverview;
  isUpcoming: boolean;
  onOpenCalendar: () => void;
}

/** The pre-save services in the order of the design ("Spotify 168 · Apple Music 31 · Deezer 13"). */
function serviceName(key: string): string {
  const names: Record<string, string> = {
    spotify: 'Spotify',
    appleMusic: 'Apple Music',
    deezer: 'Deezer',
    tidal: 'Tidal',
    amazonMusic: 'Amazon Music',
    youtubeMusic: 'YouTube Music',
    email: 'Email',
  };
  return names[key] ?? key;
}

/**
 * Done (design b1, b2): what already went out for the release: the posts published (a post belongs
 * to the release by its asset or its SmartLink, owner answer 6), the SmartLink clicks and the
 * pre-saves, then the two newest posts. It hides when nothing went out.
 */
const DonePanelComponent: React.FC<DonePanelProps> = ({ overview, isUpcoming, onOpenCalendar }) => {
  const intl = useIntl();
  const posted = useMemo(() => overview.posts.filter((post) => post.status === 'posted'), [overview.posts]);
  const failed = useMemo(() => overview.posts.filter((post) => post.status === 'failed').length, [overview.posts]);
  const byPlatform = useMemo(() => {
    const counts = new Map<string, number>();
    posted.forEach((post) => counts.set(post.platform, (counts.get(post.platform) ?? 0) + 1));
    return Array.from(counts.entries())
      .map(([platform, count]) => `${platformMeta[platform as SocialPlatform]?.name ?? platform} ${count}`)
      .join(' · ');
  }, [posted]);
  const since = useMemo(() => {
    const first = posted.map((post) => post.postedAt ?? post.scheduledAt).sort()[0];
    return first === undefined ? null : intl.formatDate(new Date(first), { day: 'numeric', month: 'short' });
  }, [intl, posted]);
  const services = useMemo(
    () =>
      Object.entries(overview.presave?.byService ?? {})
        .sort((a, b) => b[1] - a[1])
        .map(([key, count]) => `${serviceName(key)} ${intl.formatNumber(count)}`)
        .join(' · '),
    [intl, overview.presave]
  );
  const clicks = overview.smartLink?.clicks ?? 0;
  const saves = overview.presave?.saves ?? 0;

  if (posted.length === 0 && clicks === 0 && saves === 0) {
    return null;
  }

  const failedLine = failed > 0 ? ` · ${intl.formatMessage(overviewMessages.failedPosts, { count: failed })}` : '';
  return (
    <Panel as="section" data-testid="release-done">
      <PanelHead
        title={intl.formatMessage(overviewMessages.doneTitle)}
        subtitle={intl.formatMessage(overviewMessages.doneSubtitle)}
        right={
          since === null ? undefined : (
            <span className={styles.meta}>{intl.formatMessage(overviewMessages.doneSince, { date: since })}</span>
          )
        }
      />
      <PanelBody>
        <div className={styles.kpis}>
          <Panel nested className={styles.kpi}>
            <span className={styles.kpiLabel}>{intl.formatMessage(overviewMessages.postsPublished)}</span>
            <b className={styles.kpiValue}>{intl.formatNumber(posted.length)}</b>
            <span className={styles.kpiNote}>{`${byPlatform}${failedLine}`}</span>
          </Panel>
          {overview.smartLink !== null && (
            <Panel nested className={styles.kpi}>
              <span className={styles.kpiLabel}>{intl.formatMessage(overviewMessages.smartLinkClicks)}</span>
              <b className={styles.kpiValue}>{overview.smartLink.clicks === null ? '–' : intl.formatNumber(clicks)}</b>
              {overview.smartLink.clicksLast7Days !== null && (
                <span className={styles.kpiNote}>
                  {intl.formatMessage(overviewMessages.clicksThisWeek, {
                    count: intl.formatNumber(overview.smartLink.clicksLast7Days),
                  })}
                </span>
              )}
            </Panel>
          )}
          {overview.presave !== null && (
            <Panel nested className={styles.kpi}>
              <span className={styles.kpiLabel}>{intl.formatMessage(overviewMessages.presaves)}</span>
              <b className={styles.kpiValue}>{intl.formatNumber(saves)}</b>
              <span className={styles.kpiNote}>
                {isUpcoming ? services : intl.formatMessage(overviewMessages.presavesEnded)}
              </span>
            </Panel>
          )}
        </div>
        {posted.length > 0 && (
          <div className={styles.postList}>
            {posted.slice(0, 2).map((post) => (
              <OverviewPostRow
                key={post.uuid}
                post={post}
                action={{ label: intl.formatMessage(overviewMessages.viewPosts), onClick: onOpenCalendar }}
              />
            ))}
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const DonePanel = TypedMemo(DonePanelComponent);
